import React from "react";
import { Card, FullscreenTakeover } from "@jsq/ds/core";
import { Column } from "@jsq/ds/layouts";

interface BaseFullScreenTakeoverProps {
  title: string;
  onExit: () => void;
  children: React.ReactNode;
}

/**
 * BaseFullScreenTakeover Component
 *
 * This is a container component that provides a full-screen takeover layout with a header and body.
 *
 * @component
 *
 * @param {Object} props - The props for the component.
 * @param {string} props.title - The title to be displayed in the header of the FullscreenTakeover.
 * @param {function} props.onExit - The function to be called when the exit button in the header is clicked.
 * @param {React.ReactNode} props.children - The content to be displayed inside the Card component within the FullscreenTakeover body.
 *
 * @example
 * // Example usage of BaseFullScreenTakeover
 * <BaseFullScreenTakeover title="Example Title" onExit={handleExit}>
 *   <YourComponent />
 * </BaseFullScreenTakeover>
 *
 * @remarks
 * This component includes a Card component to wrap its children. Therefore, all sub-components passed as children should not have their own Card wrapper to avoid nested Card components.
 */
export const BaseFullScreenTakeover = ({
  title,
  onExit,
  children,
}: BaseFullScreenTakeoverProps) => {
  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header onExit={onExit} title={title} />
      <FullscreenTakeover.Body>
        <Column size="sm">
          <Card>{children}</Card>
        </Column>
      </FullscreenTakeover.Body>
    </FullscreenTakeover>
  );
};
